/**
 * SuperBrain Landing Page - Custom Design
 * © 2026 A-1 Affiliate Marketing LLC - All Rights Reserved
 * Faith. Service. Integrity.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AuthPage from './AuthPage';
import LanguageSwitcher from './LanguageSwitcher';
import './LandingPage.css';

const LandingPage = ({ onAuthenticated }) => {
  const { t } = useTranslation();
  const [showAuth, setShowAuth] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  if (showAuth) {
    return <AuthPage onAuthenticated={onAuthenticated} initialPlan={selectedPlan} initialMode="register" />;
  }

  const handleGetStarted = (plan) => {
    setSelectedPlan(plan);
    setShowAuth(true);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="landing-page-v2">
      {/* Header Navigation */}
      <div className="header">
        <span className="header-title">SuperBrain Agentic Aggregate</span>
        <div className="header-nav">
          <a onClick={() => scrollToSection('features')}>{t('nav.features')}</a>
          <a onClick={() => scrollToSection('compare')}>{t('nav.compare')}</a>
          <a onClick={() => scrollToSection('pricing')}>{t('nav.pricing')}</a>
          <a onClick={() => scrollToSection('about')}>{t('nav.about')}</a>
          <Link to="/login" className="header-login-link">Sign In</Link>
          <LanguageSwitcher />
        </div>
      </div>

      {/* Original project artwork: crucifix, A-1 logo, and Eagle Globe and Anchor emblem. */}
      <div className="hero">
        <div className="hero-icons-row" data-testid="hero-icons-row">
          <div className="hero-icon-frame hero-icon-frame--crucifix">
            <img src="/assets/crucifix.png" className="hero-icon-crucifix" alt="Crucifix" />
          </div>
          <div className="hero-icon-frame hero-icon-frame--a1">
            <img
              src="/assets/a1-affiliate-logo.png"
              className="hero-icon-a1"
              alt="A-1 Affiliate Marketing LLC logo"
            />
          </div>
          <div className="hero-icon-frame hero-icon-frame--ega">
            <img
              src="/assets/eagle-globe-anchor-transparent.png"
              className="hero-icon-ega"
              alt="Eagle, Globe, and Anchor emblem"
            />
          </div>
        </div>
        <div className="hero-blessing" data-testid="hero-blessing">✝ GOD BLESS EVERYONE ✝</div>
        <div className="hero-center">
          <h1>SuperBrain Agentic Aggregate</h1>
          <p className="hero-puffery">Compare AI perspectives. Review the sources.</p>
          <div className="hero-tagline">AI research, six ways at once.</div>
          <div className="hero-subtagline">Faith · Service · Integrity</div>
        </div>
      </div>

      <div className="gold-line"></div>

      {/* Main Headline */}
      <div className="main-headline">
        <h2>Stop guessing which AI to trust. Ask all six.</h2>
        <div className="accuracy-hero-badge" data-testid="accuracy-hero-badge">
          Multiple AI responses · Citations when available · Differences may be noted
        </div>
        <p>
          SuperBrain can send a question to <span className="highlight">six configured AI models in parallel</span> —
          including models from Anthropic, OpenAI, Google, xAI, Perplexity, and Mistral — then use a seventh
          synthesis step to combine the available responses. Citations and differences may be shown when available.
          Agreement is not a guarantee that an answer is correct.
        </p>
        <p className="positioning-line">
          Use it to explore research questions, plans, code, and other topics. AI output can be wrong and is not professional advice.
        </p>

        {/* Ask SuperBrain CTA */}
        <div className="ask-superbrain-cta" data-testid="ask-superbrain-cta">
          <button
            className="ask-superbrain-btn"
            onClick={() => handleGetStarted('professional')}
            data-testid="ask-superbrain-btn"
          >
            🧠 Ask SuperBrain →
          </button>
          <div className="ask-subline">
            🎁 Free 3-day trial · Card on file · Cancel anytime · $0 if you cancel before Day 4
          </div>
        </div>
      </div>

      {/* Demo video — hidden until real demo content is uploaded.
          The previous Rickroll placeholder was misleading and has been removed. */}

      {/* AI Model Badges */}
      <div className="ai-models">
        <div className="ai-chip chip-claude">Claude</div>
        <div className="ai-chip chip-gpt">GPT-5</div>
        <div className="ai-chip chip-gemini">Gemini</div>
        <div className="ai-chip chip-grok">Grok</div>
        <div className="ai-chip chip-perplexity">Perplexity</div>
        <div className="ai-chip chip-mistral">Mistral</div>
      </div>

      <div className="gold-line"></div>

      {/* Founder's Note */}
      <section id="about" className="founders-note" data-testid="founders-note">
        <div className="founders-note-inner">
          <div className="founders-note-eyebrow">A Note From The Founder</div>
          <blockquote className="founders-note-quote">
            <p>
              I wanted a way to compare responses from multiple AI models instead
              of relying on only one. So I built <strong>SuperBrain</strong> — a
              service that combines available responses and may show citations,
              disagreements, or an estimated confidence score. These features
              do not guarantee that an answer is correct.
            </p>
            <p className="founders-note-signoff">
              I hope you enjoy using it.
            </p>
          </blockquote>
          <div className="founders-note-attribution">
            <div className="founders-note-name">— The Founder</div>
            <div className="founders-note-company">A-1 Affiliate Marketing LLC · USMC Veteran Owned · Catholic Owned</div>
          </div>
        </div>
      </section>

      <div className="gold-line"></div>

      {/* Features Section */}
      <div id="features" className="features">
        <div className="feature-card">
          <h3>Multi-Model Aggregation</h3>
          <p>Send a question to configured AI models in parallel and review their responses and any citations provided.</p>
        </div>

        <div className="feature-card">
          <h3>Intelligent Synthesis</h3>
          <p>A synthesis step summarizes available responses. It can still miss information or repeat an error, so review important claims.</p>
        </div>

        <div className="feature-card">
          <h3>Account and billing</h3>
          <p>Account features are available in the service, and subscription payments are processed through Stripe. See our Privacy Policy for data-handling details.</p>
        </div>

        <div className="feature-card">
          <h3>Parallel requests</h3>
          <p>Model requests can run in parallel. Response time depends on provider availability, network conditions, and the selected models.</p>
        </div>

        <div className="feature-card">
          <h3>Built for Professionals</h3>
          <p>Explore research, coding, planning, and other topics. AI output is informational and should be checked before you rely on it.</p>
        </div>

        <div className="feature-card">
          <h3>Veteran Built & Owned</h3>
          <p>Built with Marine Corps discipline and Catholic values. Quality, integrity, and service — Semper Fidelis.</p>
        </div>
      </div>

      <div className="gold-line"></div>

      {/* Meet The Council — specialized AI roles */}
      <section className="council-section" data-testid="council-section">
        <div className="council-inner">
          <h2>Meet The Council</h2>
          <p className="council-sub">Configured AI providers respond to a question, and a separate synthesis step reviews the available replies.</p>

          <div className="council-grid">
            <div className="council-card" data-testid="council-role-researcher">
              <div className="council-role">Researcher</div>
              <div className="council-model">Claude Opus 4.6</div>
              <div className="council-desc">Can draft a research-oriented response and include sources when available.</div>
            </div>
            <div className="council-card" data-testid="council-role-critic">
              <div className="council-role">Critic</div>
              <div className="council-model">GPT-5.2</div>
              <div className="council-desc">Can offer counterpoints and identify assumptions for you to review.</div>
            </div>
            <div className="council-card" data-testid="council-role-strategist">
              <div className="council-role">Strategist</div>
              <div className="council-model">Gemini 3.1 Pro</div>
              <div className="council-desc">Can organize options and describe potential trade-offs.</div>
            </div>
            <div className="council-card" data-testid="council-role-outside-voice">
              <div className="council-role">Outside Voice</div>
              <div className="council-model">Grok-4</div>
              <div className="council-desc">Can provide another perspective; it may not have current or complete information.</div>
            </div>
            <div className="council-card" data-testid="council-role-fact-checker">
              <div className="council-role">Fact-Checker</div>
              <div className="council-model">Perplexity Sonar Pro</div>
              <div className="council-desc">May provide web-related sources. Check each source and confirm it supports the claim.</div>
            </div>
            <div className="council-card" data-testid="council-role-generalist">
              <div className="council-role">Generalist</div>
              <div className="council-model">Mistral Large</div>
              <div className="council-desc">Can provide a shorter response to straightforward questions.</div>
            </div>
          </div>

            <div className="council-master" data-testid="council-master">
              <div className="council-master-badge">Master Synthesizer</div>
              <h3>DeepSeek V4 Pro</h3>
              <p>A separate synthesis step reviews the responses returned for a question and produces a combined response. Citations, disagreement notes, and confidence estimates may appear when available; none guarantees accuracy.</p>
            </div>
        </div>
      </section>

      <div className="gold-line"></div>

      {/* Plain-language description of the product workflow. */}
      <div id="compare" className="compare-section" data-testid="compare-section">
        <h2>How SuperBrain works</h2>
        <p className="compare-sub">
          A short overview of the workflow. Provider and model availability can change.
        </p>

        <div className="compare-table-wrap">
          <table className="compare-table">
            <thead>
              <tr>
                <th>Step</th>
                <th>What to expect</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="row-label">Ask</td>
                <td>A question can be routed to configured model providers.</td>
              </tr>
              <tr>
                <td className="row-label">Review responses</td>
                <td>A synthesis step combines responses returned by available models.</td>
              </tr>
              <tr>
                <td className="row-label">Check sources</td>
                <td>Citations or differences may be included. Verify important claims independently.</td>
              </tr>
              <tr>
                <td className="row-label">Use judgment</td>
                <td>AI output may be incomplete or incorrect and is not professional advice.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="gold-line"></div>
      <div id="pricing" className="pricing">
        <h2>Membership Plans</h2>
        <p className="pricing-summary-sub">Four tiers · 3-day free trial · Cancel anytime</p>

        <div className="pricing-summary-grid">
          <div className="price-summary-card">
            <div className="price-tier">PROFESSIONAL</div>
            <div className="price-amount">$59<span>/mo</span></div>
            <div className="price-summary-desc">225 queries/mo · Full 7-AI Council · Tools + memory · 25 images</div>
          </div>

          <div className="price-summary-card featured">
            <div className="price-trial">Most Popular</div>
            <div className="price-tier">BUSINESS</div>
            <div className="price-amount">$99<span>/mo</span></div>
            <div className="price-summary-desc">400 queries/mo · Full 7-AI Council · 75 images · Live chat</div>
          </div>

          <div className="price-summary-card">
            <div className="price-tier">ENTERPRISE</div>
            <div className="price-amount">$249<span>/mo</span></div>
            <div className="price-summary-desc">1,000 queries/mo · 250 images · Onboarding call · Analytics</div>
          </div>

          <div className="price-summary-card">
            <div className="price-tier">ULTIMATE</div>
            <div className="price-amount">$499<span>/mo</span></div>
            <div className="price-summary-desc">2,000 queries/mo · 750 images · Account manager · Quarterly review</div>
          </div>
        </div>

        <button
          className="price-btn pricing-summary-cta"
          onClick={() => { window.location.href = '/pricing'; }}
          data-testid="landing-see-full-pricing"
        >
          See Full Plan Details & FAQs →
        </button>

        <p className="pricing-summary-yearly">Yearly available — save 2 months on every tier.</p>
      </div>

      <div className="gold-line"></div>

      {/* Footer */}
      <footer className="landing-footer-v2">
        <div className="footer-brand">
          <span className="footer-text">Catholic Owned · USMC Veteran Owned</span>
        </div>
        <div className="footer-blessing">GOD BLESS EVERYONE</div>
        <div className="footer-part-of">Part of</div>
        <div className="footer-logo-section">
          <img
            src="/assets/a1-affiliate-logo.png"
            alt="A-1 Affiliate Marketing LLC logo"
            className="footer-original-mark"
          />
          <div className="footer-logo-center">
            <div className="footer-company">A-1 Affiliate Marketing LLC</div>
          </div>
        </div>
        
        {/* Social Media & Contact Links */}
        <div className="footer-social">
          <a href="https://facebook.com/superbrainai" target="_blank" rel="noopener noreferrer" className="social-link">
            Facebook
          </a>
          <span className="separator">·</span>
          <a href="https://twitter.com/superbrainai" target="_blank" rel="noopener noreferrer" className="social-link">
            Twitter
          </a>
          <span className="separator">·</span>
          <a href="https://linkedin.com/company/superbrain" target="_blank" rel="noopener noreferrer" className="social-link">
            LinkedIn
          </a>
          <span className="separator">·</span>
          <a href="mailto:a1affiliatemarketingllc@gmail.com" className="social-link">
            Contact Us
          </a>
        </div>
        
        {/* Legal Links */}
        <div className="footer-legal">
          <a href="/terms" target="_blank" rel="noopener noreferrer" data-testid="footer-terms-link">Terms of Service</a>
          <span className="separator">·</span>
          <a href="/privacy" target="_blank" rel="noopener noreferrer" data-testid="footer-privacy-link">Privacy Policy</a>
          <span className="separator">·</span>
          <a href="/refund" target="_blank" rel="noopener noreferrer" data-testid="footer-refund-link">Refund Policy</a>
          <span className="separator">·</span>
          <a href="/disclaimer" target="_blank" rel="noopener noreferrer" data-testid="footer-disclaimer-link">Disclaimer</a>
          <span className="separator">·</span>
          <a href="/dmca" target="_blank" rel="noopener noreferrer" data-testid="footer-dmca-link">DMCA</a>
          <span className="separator">·</span>
          <a href="https://superbrain.life" target="_blank" rel="noopener noreferrer">Official Website</a>
        </div>
        
        {/* Copyright Notice */}
        <div className="footer-copyright">
          © 2026 A-1 Affiliate Marketing LLC. All Rights Reserved.
          <br />
          <span className="disclaimer-text">
            AI responses are for informational purposes only. Not professional advice.
          </span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
